import { BaseService } from './base.service';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ApiVersions } from './api-version';
import { EvaluationDefinitionSummary } from '../models/evaluation/evaluationDefinitionSummary';
import { EvaluationDefinition } from '../models/evaluation-definition/evaluationDefinition';
import { AttributeDefinition } from '../models/attributes/attributeDefinition';
import { map } from 'rxjs/operators';
import { EvaluationDefinitionClone } from '../models/evaluation-definition/evaluation-definition-clone';
import { EvaluationDefinitionEdit } from '../models/evaluation-definition/evaluation-definition-edit';
import { EvaluationSummary } from '../models/team/evaluationSummary';

@Injectable({
  providedIn: 'root'
})
export class EvaluationDefinitionService extends BaseService {
  private basePath = 'evaluation-definitions';
  public evaluationDefinitions = new Subject<EvaluationDefinitionSummary[]>();
  public sendableEvaluationDefinitions = new Subject<EvaluationDefinitionSummary[]>();
  public evaluationDefinition = new Subject<EvaluationDefinition>();
  public evaluationDefinitionAttributes = new BehaviorSubject<AttributeDefinition[]>(null);
  private _selectedEvaluationDefinition: EvaluationDefinition;
  private _isSaving = false;

  public get isSaving() {
    return this._isSaving;
  }

  constructor() {
    super();
  }

  getAll() {
    this.get<EvaluationDefinitionSummary[]>(this.basePath)
      .pipe(
        map((data) =>
          data.map((app) => {
            return EvaluationDefinitionSummary.parse(app);
          })
        )
      )
      .subscribe((data) => {
        this.evaluationDefinitions.next(data);
      });
  }

  getSendable() {
    this.get<EvaluationDefinitionSummary[]>(`${this.basePath}/sendable`)
      .pipe(
        map((data) =>
          data.map((app) => {
            return EvaluationDefinitionSummary.parse(app);
          })
        )
      )
      .subscribe((data) => {
        this.sendableEvaluationDefinitions.next(data);
      });
  }

  getOne(evaluationId: string) {
    this.get<EvaluationDefinition>(`${this.basePath}/${evaluationId}`, false)
      .pipe(
        map((svy) => {
          if (svy) {
            if (!svy.confirmation) {
              svy.confirmation = 'Thank you for completing the evaluation.';
            }
            return EvaluationDefinition.parse(svy);
          }
        })
      )
      .subscribe((data) => {
        this._selectedEvaluationDefinition = data;
        this.evaluationDefinition.next(this._selectedEvaluationDefinition);
        this.evaluationDefinitionAttributes.next(
          this._selectedEvaluationDefinition.questions.map((x) => x.attributeDefinition)
        );
      });
  }

  update(surveyDef: EvaluationDefinitionEdit) {
    this._isSaving = true;
    let path = `${this.basePath}/${this._selectedEvaluationDefinition.id}`;
    let body = JSON.stringify(surveyDef);

    return this.put(path, body)
      .pipe(map((response) => this._selectedEvaluationDefinition.update(response)))
      .subscribe((data) => {
        this._isSaving = false;
        this.evaluationDefinition.next(data);
      });
  }

  create(name: string) {
    let body = JSON.stringify({ title: name });
    return this.post(`${this.basePath}`, body).pipe(map((data) => data?.id));
  }

  clone(fromId: string, clone: EvaluationDefinitionClone) {
    const body = JSON.stringify(clone);
    return this.post(`${this.basePath}/${fromId}/clone`, body).pipe(map((data) => data?.id));
  }

  getEvaluationTemplates() {
    return this.get('evaluation-definitions', null, ApiVersions.v0_1);
  }

  getOpenEvaluationTemplates() {
    return this.get('evaluation-definitions/open', null, ApiVersions.v0_1);
  }

  close(id: string) {
    this.put(`${this.basePath}/${id}/close`).subscribe(() => this.getAll());
  }

  open(id: string) {
    this.put(`${this.basePath}/${id}/open`).subscribe(() => this.getAll());
  }

  archiveEvaluation(id: string): Observable<any> {
    this._isSaving = true;
    let path = `${this.basePath}/${id}/archive`;
    var obs = this.patch(path).pipe(
      map((data) => {
        if (data) {
          if (this._selectedEvaluationDefinition) {
            return this._selectedEvaluationDefinition.update(data);
          } else {
            return data;
          }
        }
      })
    );
    obs.subscribe((data) => {
      if (data) {
        this.openSnackBar(`Successfully archived ${data.title}`, true);
        this._isSaving = false;
        this.evaluationDefinition.next(data);
      }
    });

    return obs;
  }

  unarchiveEvaluation(id: string): Observable<any> {
    this._isSaving = true;
    let path = `${this.basePath}/${id}/unarchive`;
    var obs = this.patch(path).pipe(
      map((data) => {
        if (data) {
          if (this._selectedEvaluationDefinition) {
            return this._selectedEvaluationDefinition.update(data);
          } else {
            return data;
          }
        }
      })
    );
    obs.subscribe((data) => {
      if (data) {
        this.openSnackBar(`Successfully unarchived ${data.title}`, true);
        this._isSaving = false;
        this.evaluationDefinition.next(data);
      }
    });

    return obs;
  }

  getEvaluationSummaryReport(): Observable<EvaluationSummary[]> {
    return this.get(this.basePath.concat('/summary-report'), true, ApiVersions.v0_1);
  }
}
